import { createApiClient } from '@acme/api-client'
import type { AppliedTenantTheme } from '@acme/app-shell'
import { inject, ref, type InjectionKey, type Ref } from 'vue'
import type {
  CustomerDecisionRequest,
  CustomerPortalSession,
  CustomerTicketDetail,
  CustomerTicketSummary,
  TicketType,
} from '@/api/erp/types'
import type { components } from '@/api/schema'
import { readToken } from './customerAccess'

export type PublicBranding = components['schemas']['PublicBrandingResponse']

export interface NewCustomerTicket {
  title: string
  description: string
  type: TicketType
  customerId: string | null
  files: File[]
}

// The customer's page talks to the ERP's public routes (/api/erp/public/support/*) with the access link in
// X-Customer-Access, never with a login; the institution's look comes from the assistente's public branding.
export function createCustomerPortalApi(erpBaseUrl: string, apiBaseUrl: string) {
  const token: Ref<string | null> = ref(readToken())
  const client = createApiClient({ baseUrl: erpBaseUrl, getAccessToken: () => null })
  const root = apiBaseUrl.replace(/\/+$/, '')
  const headers = () => ({ 'X-Customer-Access': token.value ?? '' })
  const path = (suffix = '') => `/public/support${suffix}`

  async function json<T>(url: string): Promise<T | null> {
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      return response.ok ? ((await response.json()) as T) : null
    } catch {
      return null
    }
  }

  return {
    token,
    requestAccess: (tenantId: string, email: string) =>
      client.post<void>(path('/access'), { tenantId, email }),
    session: (signal?: AbortSignal) =>
      client.get<CustomerPortalSession>(path('/me'), { headers: headers(), signal }),
    tickets: (signal?: AbortSignal) =>
      client.get<CustomerTicketSummary[]>(path('/tickets'), { headers: headers(), signal }),
    ticket: (id: string, signal?: AbortSignal) =>
      client.get<CustomerTicketDetail>(path(`/tickets/${id}`), { headers: headers(), signal }),
    createTicket: (input: NewCustomerTicket) => {
      const form = new FormData()
      form.append('title', input.title)
      form.append('description', input.description)
      form.append('type', input.type)
      if (input.customerId) form.append('customerId', input.customerId)
      for (const file of input.files) form.append('files', file, file.name)
      return client.post<CustomerTicketDetail>(path('/tickets'), form, { headers: headers() })
    },
    reply: (id: string, body: string, files: File[]) => {
      const form = new FormData()
      form.append('body', body)
      for (const file of files) form.append('files', file, file.name)
      return client.post<CustomerTicketDetail>(path(`/tickets/${id}/replies`), form, { headers: headers() })
    },
    decideQuote: (id: string, body: CustomerDecisionRequest) =>
      client.post<CustomerTicketDetail>(path(`/tickets/${id}/quote-decision`), body, { headers: headers() }),
    decideAcceptance: (id: string, body: CustomerDecisionRequest) =>
      client.post<CustomerTicketDetail>(path(`/tickets/${id}/acceptance`), body, { headers: headers() }),
    attachment: (id: string, attachmentId: string) =>
      client.download(path(`/tickets/${id}/attachments/${attachmentId}`), { headers: headers() }),
    branding: (tenantId: string) =>
      json<PublicBranding>(`${root}/public/tenants/${encodeURIComponent(tenantId)}/branding`),
    theme: (tenantId: string) =>
      json<AppliedTenantTheme>(`${root}/public/tenants/${encodeURIComponent(tenantId)}/theme`),
    logoUrl: (tenantId: string, updatedAtUtc: string | null | undefined) =>
      updatedAtUtc
        ? `${root}/public/tenants/${encodeURIComponent(tenantId)}/logo?v=${encodeURIComponent(updatedAtUtc)}`
        : null,
  }
}

export type CustomerPortalApi = ReturnType<typeof createCustomerPortalApi>

export const CUSTOMER_PORTAL_API: InjectionKey<CustomerPortalApi> = Symbol('CustomerPortalApi')

export function useCustomerPortalApi(): CustomerPortalApi {
  const api = inject(CUSTOMER_PORTAL_API)
  if (!api) throw new Error('CustomerPortalApi not provided')
  return api
}

// What the ticket screens use: the customer's page (CUSTOMER_PORTAL_API) or the Suporte of an institution inside the
// portal (CUSTOMER_TICKETS_API, the same answers by the logged-in routes /tenants/{t}/support/*).
export type CustomerTicketsApi = Pick<CustomerPortalApi, 'tickets' | 'ticket' | 'createTicket' | 'reply' | 'decideQuote' | 'decideAcceptance' | 'attachment'>

export const CUSTOMER_TICKETS_API: InjectionKey<CustomerTicketsApi> = Symbol('CustomerTicketsApi')

export function useCustomerTicketsApi(): CustomerTicketsApi {
  const api = inject(CUSTOMER_TICKETS_API, null) ?? inject(CUSTOMER_PORTAL_API, null)
  if (!api) throw new Error('CustomerTicketsApi not provided')
  return api
}
